// Shrink customer photos in the browser before they are uploaded.
//
// Flagship phones shoot 48-200 MP (8-25 MB JPEG, HEIC on iPhone). The pipeline never needs more than a few
// thousand pixels (face detection, then image models that work at ~1-2K), so a full-size photo only made
// uploads slow on mobile data and hit the old 5-10 MB caps. normalizePhoto() decodes the photo (EXIF rotation
// applied), scales the long edge to MAX_PHOTO_EDGE and re-encodes it as a high-quality JPEG. Small photos that
// already fit are passed through untouched.

/** Largest photo we accept from the device, before shrinking. */
export const MAX_RAW_PHOTO_BYTES = 60 * 1024 * 1024;
/** Server-side cap (safety net for a browser that cannot shrink the photo). */
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
/** Long edge after shrinking: ample for face crops and for the image models. */
export const MAX_PHOTO_EDGE = 3072;
const JPEG_QUALITY = 0.9;
/** Files above this are re-encoded even when their dimensions already fit. */
const PASS_THROUGH_MAX_BYTES = 8 * 1024 * 1024;
const SERVER_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

/** `accept` for photo inputs: the server types plus iPhone HEIC (converted to JPEG in the browser). */
export const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif';

export function targetSize(width: number, height: number, maxEdge = MAX_PHOTO_EDGE): { width: number; height: number } {
  const long = Math.max(width, height);
  if (long <= maxEdge) return { width, height };
  const scale = maxEdge / long;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

export function needsReencode(file: { type: string; size: number }, width: number, height: number): boolean {
  if (!SERVER_TYPES.has(file.type)) return true;
  if (Math.max(width, height) > MAX_PHOTO_EDGE) return true;
  return file.size > PASS_THROUGH_MAX_BYTES;
}

export class PhotoTooLargeError extends Error {}
export class PhotoUnreadableError extends Error {}

/**
 * Browser only. Returns a File the server accepts: the original when it already fits, otherwise a shrunk JPEG.
 * Throws PhotoTooLargeError for files over MAX_RAW_PHOTO_BYTES and PhotoUnreadableError when the browser cannot
 * decode a format the server would also refuse (e.g. HEIC outside Safari).
 */
export async function normalizePhoto(file: File): Promise<File> {
  if (file.size > MAX_RAW_PHOTO_BYTES) throw new PhotoTooLargeError('photo too large');
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    if (SERVER_TYPES.has(file.type) && file.size <= MAX_UPLOAD_BYTES) return file; // let the server judge it
    throw new PhotoUnreadableError('photo could not be read');
  }
  try {
    if (!needsReencode(file, bitmap.width, bitmap.height)) return file;
    const { width, height } = targetSize(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob) return file;
    const name = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } finally {
    bitmap.close();
  }
}
